import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Select from 'react-select';
import moment from 'moment-timezone';
import {
  Container,
  Row,
  Col,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Button,
} from 'reactstrap';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import {
  FiChevronDown,
  FiChevronUp,
  FiChevronLeft,
  FiChevronRight,
} from 'react-icons/fi';
import { timelineActionTypes } from '@store/Timeline/Timeline.actionTypes';
import { useMemberCompanyTimezones } from '../../hooks/useMemberCompanyTimezones';
import { useTeamMemberDetailsForSelect } from '../../hooks/useTeamMemberDetailsForSelect';
import memberSessionsService from '../../services/memberSessions';
import './styles.scss';
import styles from './Screenshots.module.scss';
import './styles.scss';
import { Dropdown } from '@components/Dropdown';
import classNames from 'classnames';
import { fontWeightEnum, Text, textVariantsEnum } from '@/components/Text';
import { Accordion } from '@/components/Accordion';
import { TimeFilter } from '@/components/TimeFilter/TimeFilter.component';
import { IoRefresh } from 'react-icons/io5';
import { Modal as CustomModal } from '@components/Modal';
import { ScreenshotsModal } from './ScreenshotsModal.component';
import { formatPercent } from '@/utils/numberUtils';
import { formatTimeFromSeconds } from '@/utils/timeUtils';
import { normalizeIdleForDisplay } from '@/utils/idleDisplayUtils';
import { filterScreenshotsByTimeRange } from './Screenshots.helper';
import { SessionSourceTags } from '@/components/SessionSourceTags';
import {
  useDashboardOverviewUrlWrite,
  useReportFiltersPopAndMemberHydration,
} from '@/hooks/useDashboardOverviewUrlSync';
import { getInitialReportDateRangeFromSearch } from '@/utils/dashboardOverviewUrlFilters';
import {
  collectScreenshotsFromActivities,
  getScreenshotFullSrc,
  getScreenshotPreviewSrc,
  getScreenshotRevealKey,
} from '@/utils/screenshotPrivacy';

const getDayLabel = ({ dayLabel, dayKey }) => {
  let additionalText = '';
  const isToday = moment().format('YYYY-MM-DD') === dayKey;
  const isYesterday =
    moment().subtract(1, 'day').format('YYYY-MM-DD') === dayKey;
  if (isToday) {
    additionalText = 'Today';
  } else if (isYesterday) {
    additionalText = 'Yesterday';
  }
  return (
    <div
      className={classNames(styles.dayLabel, {
        [styles.dayLabel_today]: isToday,
      })}
    >
      <Text
        variant={textVariantsEnum.Text_xs}
        fontWeight={fontWeightEnum.Medium}
      >
        {additionalText} {dayLabel}
      </Text>
      <div className={styles.dividerLine} />
    </div>
  );
};
const getTimeRange = (startTime, endTime, duration, session) => {
  return (
    <div className={styles.timeRangeContainer}>
      <div className={styles.timeRangePrimary}>
        <Text
          variant={textVariantsEnum.Text_13}
          fontWeight={fontWeightEnum.Normal}
        >
          {startTime} to {endTime}
        </Text>
        <SessionSourceTags
          pastData={session?.pastData}
          typeLog={session?.typeLog}
        />
      </div>
      <Text
        variant={textVariantsEnum.Text_xxs}
        fontWeight={fontWeightEnum.Light}
      >
        (Duration: {duration})
      </Text>
    </div>
  );
};

const getTimeStatistics = (idlePercentage, activityPercentage) => {
  return (
    <div className={styles.timeStatisticsContainers}>
      <Text
        variant={textVariantsEnum.Text_xxs}
        fontWeight={fontWeightEnum.Light}
        className={styles.idel}
      >
        Idle: {idlePercentage}
      </Text>
      <Text
        variant={textVariantsEnum.Text_xxs}
        fontWeight={fontWeightEnum.Light}
        className={styles.activity}
      >
        | Activity: {formatPercent(activityPercentage)}
      </Text>
    </div>
  );
};
const getScreenshots = (
  screenshots,
  selectedTimezone,
  openScreenshotModal,
  isLoading
) => {
  if (isLoading) {
    return (
      <div className={styles.screenshotsLoading}>
        <Spinner size="sm" color="primary" />
        <span className="ml-2">Loading screenshots...</span>
      </div>
    );
  }
  if (screenshots?.length === 0) {
    return (
      <div className={styles.noScreenshots}>
        No screenshots available for this session.
      </div>
    );
  }
  return (
    <div className={classNames(styles.screenshotsRow, 'with-scroll')}>
      {(screenshots ?? []).map((screenshot, idx) => {
        const revealKey = getScreenshotRevealKey(screenshot);
        return (
          <div key={revealKey || idx} className={styles.screenshotItem}>
            <div
              className={styles.imageContainer}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openScreenshotModal(idx, screenshots);
                }
              }}
              onClick={() => {
                openScreenshotModal(idx, screenshots);
              }}
            >
              <img
                src={getScreenshotPreviewSrc(screenshot)}
                alt={`Screenshot ${idx + 1}`}
                className={styles.image}
                loading="lazy"
                onError={(e) => {
                  e.target.src =
                    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect fill='%23f3f4f6' width='100' height='100'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%239ca3af' font-size='12'%3EImage not available%3C/text%3E%3C/svg%3E";
                  e.target.onerror = null;
                }}
              />
            </div>

            <div className={styles.footer}>
              <Text
                variant={textVariantsEnum.Text_5}
                fontWeight={fontWeightEnum.Semibold}
                className={styles.timeStamp}
              >
                {screenshot.timestamp
                  ? moment(screenshot.timestamp)
                      .tz(selectedTimezone)
                      .format('hh:mm A')
                  : null}
              </Text>
            </div>
          </div>
        );
      })}
    </div>
  );
};
export const ScreenshotsComponent = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { sessionsByDay, loading, filters } = useSelector(
    (state) => state.timeline
  );
  const team = useSelector((state) => state.team);
  const auth = useSelector((state) => state.auth);

  const teamId = team?.active?.activeTeamid;
  const companyId = auth?.profile?.activeCompany?.companyId;
  const [selectedMember, setSelectedMember] = useState(null);
  const memberId = selectedMember?.value || null;

  const {
    memberDetails,
    isLoading: membersLoading,
    isError: membersError,
    error: membersErrorObj,
    refetch: refetchMembers,
  } = useTeamMemberDetailsForSelect(teamId);

  const membersOptions = useMemo(() => {
    if (!memberDetails || typeof memberDetails !== 'object') return [];
    return Object.entries(memberDetails)
      .filter(([, m]) => !m?.deleted)
      .map(([uid, m]) => ({
        value: uid,
        label: m.employeeId
          ? `${m.userName || uid} (${m.employeeId})`
          : m.userName || uid,
        employeeId: m.employeeId,
        rate: m.rates ?? 0,
      }))
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }, [memberDetails]);

  const membersOptionsRef = useRef(membersOptions);
  membersOptionsRef.current = membersOptions;

  const [range, setRange] = useState(() =>
    getInitialReportDateRangeFromSearch(
      typeof window !== 'undefined' ? window.location.search : ''
    )
  );

  const {
    urlTimezoneForHook,
    userOverrideTimezoneRef,
    setIgnoreUrlTimezone,
    onMemberFilterChange,
  } = useReportFiltersPopAndMemberHydration({
    history,
    location,
    teamId,
    currentUserId: auth?.profile?.id,
    membersOptions,
    selectedMember,
    setSelectedMember,
    setDateRange: setRange,
    membersOptionsRef,
  });

  const {
    timezoneOptions,
    selectedTimezone,
    setSelectedTimezone,
    timezonesLoading,
  } = useMemberCompanyTimezones({
    teamId,
    memberId,
    companyId,
    initialTimezone: urlTimezoneForHook || filters?.timeZone || null,
    autoSelectMemberTimezone: true,
    userOverrideTimezoneRef,
  });

  useDashboardOverviewUrlWrite({
    teamId,
    memberId: selectedMember?.value,
    dateRange: range,
    selectedTimezone,
    history,
    location,
  });

  const handleTimezoneFilterChange = useCallback(
    (value) => {
      userOverrideTimezoneRef.current = true;
      setIgnoreUrlTimezone(true);
      setSelectedTimezone(value);
    },
    [setSelectedTimezone, setIgnoreUrlTimezone, userOverrideTimezoneRef]
  );

  const [sessionScreenshots, setSessionScreenshots] = useState({});
  const [loadingScreenshots, setLoadingScreenshots] = useState({});
  const [expandedSessions, setExpandedSessions] = useState(new Set());
  const [screenshotModalOpen, setScreenshotModalOpen] = useState(false);
  const [selectedScreenshotIndex, setSelectedScreenshotIndex] = useState(0);
  const [allScreenshotsList, setAllScreenshotsList] = useState([]);

  const sessionScreenshotsRef = useRef(sessionScreenshots);
  const loadingScreenshotsRef = useRef(loadingScreenshots);
  const sessionActivitiesCacheRef = useRef({});
  sessionScreenshotsRef.current = sessionScreenshots;
  loadingScreenshotsRef.current = loadingScreenshots;

  const getSessionActivitiesCacheKey = (sessionId, timezone) =>
    `${sessionId}::${timezone || 'UTC'}`;

  const getEmployeeIdForUser = (userId) => {
    if (!userId) return '';
    const match = membersOptions.find((m) => m.value === userId);
    return match?.employeeId ?? userId;
  };

  const getRateForUser = (userId) => {
    const match = membersOptions.find((m) => m.value === userId);
    return match?.rate || 0;
  };

  const fetchTimeline = () => {
    if (!selectedMember) return;
    const employeeId = getEmployeeIdForUser(selectedMember.value);
    dispatch({
      type: timelineActionTypes.GET_SESSIONS,
      payload: {
        teamId,
        userUID: selectedMember.value,
        companyId,
        employeeId,
        memberId: selectedMember.value,
        startDate: range.start,
        endDate: range.end,
        timeZone: selectedTimezone,
        page: 1,
        pageSize: 1000, // Large page size to get all sessions
        rate: getRateForUser(selectedMember.value),
      },
    });
  };

  useEffect(() => {
    if (selectedMember) {
      // Clear screenshots and expanded sessions when filters change
      setSessionScreenshots({});
      setLoadingScreenshots({});
      setExpandedSessions(new Set());
      sessionActivitiesCacheRef.current = {};
      fetchTimeline();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMember, selectedTimezone, range.start, range.end]);

  // Fetch screenshots for a session
  const fetchSessionScreenshots = async (
    sessionId,
    parentSessionId = null,
    segmentStart = null,
    segmentEnd = null
  ) => {
    const actualSessionId = parentSessionId || sessionId;
    const isSplitSegment = parentSessionId && sessionId !== actualSessionId;
    const shotsMap = sessionScreenshotsRef.current;
    const loadingMap = loadingScreenshotsRef.current;
    const cacheKey = getSessionActivitiesCacheKey(
      actualSessionId,
      selectedTimezone
    );

    if (isSplitSegment && shotsMap[actualSessionId]) {
      const allScreenshots = shotsMap[actualSessionId];
      const filteredScreenshots = filterScreenshotsByTimeRange(
        allScreenshots,
        segmentStart,
        segmentEnd,
        selectedTimezone
      );
      setSessionScreenshots((prev) => ({
        ...prev,
        [sessionId]: filteredScreenshots,
      }));
      return;
    }

    if (shotsMap[actualSessionId] || loadingMap[actualSessionId]) {
      return;
    }

    if (!teamId || !selectedMember?.value || !actualSessionId) {
      return;
    }

    if (sessionActivitiesCacheRef.current[cacheKey]) {
      const cachedAll = sessionActivitiesCacheRef.current[cacheKey];
      setSessionScreenshots((prev) => {
        const updated = { ...prev, [actualSessionId]: cachedAll };
        if (isSplitSegment && segmentStart && segmentEnd) {
          updated[sessionId] = filterScreenshotsByTimeRange(
            cachedAll,
            segmentStart,
            segmentEnd,
            selectedTimezone
          );
        }
        return updated;
      });
      return;
    }

    setLoadingScreenshots((prev) => ({ ...prev, [actualSessionId]: true }));

    let sessionStartTime = null;
    if (sessionsByDay) {
      for (const day of sessionsByDay) {
        const foundSession = day.sessions?.find(
          (s) => s.id === actualSessionId || s.id === sessionId
        );
        if (foundSession?.start) {
          sessionStartTime = foundSession.start;
          break;
        }
      }
    }

    try {
      const employeeId = getEmployeeIdForUser(selectedMember.value);
      const result = await memberSessionsService.getSessionActivities({
        teamId,
        userUID: selectedMember.value,
        companyId,
        employeeId,
        sessionId: actualSessionId,
        sessionStartTime,
      });

      if (result.success) {
        const activities = result.data.activities || [];
        const allScreenshots = collectScreenshotsFromActivities(activities);

        sessionActivitiesCacheRef.current[cacheKey] = allScreenshots;

        setSessionScreenshots((prev) => {
          const updated = { ...prev, [actualSessionId]: allScreenshots };

          if (isSplitSegment && segmentStart && segmentEnd) {
            const filteredScreenshots = filterScreenshotsByTimeRange(
              allScreenshots,
              segmentStart,
              segmentEnd,
              selectedTimezone
            );
            updated[sessionId] = filteredScreenshots;
          }

          return updated;
        });
      }
    } catch (error) {
      console.error('Error loading session screenshots', error);
    } finally {
      setLoadingScreenshots((prev) => {
        const updated = { ...prev };
        delete updated[actualSessionId];
        if (isSplitSegment) {
          delete updated[sessionId];
        }
        return updated;
      });
    }
  };

  // Fetch screenshots only for sessions whose accordion is open
  useEffect(() => {
    if (loading || !sessionsByDay?.length) return;

    sessionsByDay.forEach((day) => {
      day.sessions.forEach((session) => {
        if (!session.id || !expandedSessions.has(session.id)) return;

        const actualSessionId = session.parentSessionId || session.id;
        const isSplitSegment = session.isSplit && session.parentSessionId;
        const shotsMap = sessionScreenshotsRef.current;
        const loadingMap = loadingScreenshotsRef.current;

        if (!shotsMap[actualSessionId] && !loadingMap[actualSessionId]) {
          fetchSessionScreenshots(
            session.id,
            session.parentSessionId,
            isSplitSegment ? session.start : null,
            isSplitSegment ? session.end : null
          );
        } else if (
          isSplitSegment &&
          !shotsMap[session.id] &&
          shotsMap[actualSessionId]
        ) {
          fetchSessionScreenshots(
            session.id,
            session.parentSessionId,
            session.start,
            session.end
          );
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, sessionsByDay, selectedTimezone, expandedSessions]);

  // Open screenshot modal
  const openScreenshotModal = (clickedIndex, screenshots) => {
    const allScreenshots = [];
    sessionsByDay.forEach((day) => {
      day.sessions.forEach((session) => {
        const sessionScreens =
          session.isSplit && session.parentSessionId
            ? sessionScreenshots[session.id] || []
            : sessionScreenshots[session.id] || [];
        sessionScreens.forEach((screenshot) => {
          allScreenshots.push({
            ...screenshot,
            sessionId: session.id,
            dayLabel: day.dayLabel,
          });
        });
      });
    });

    setAllScreenshotsList(allScreenshots);

    const clickedScreenshot = screenshots[clickedIndex];
    const foundIndex = allScreenshots.findIndex(
      (s) =>
        s.url === clickedScreenshot.url &&
        s.timestamp === clickedScreenshot.timestamp &&
        s.activityId === clickedScreenshot.activityId &&
        s.screenIndex === clickedScreenshot.screenIndex
    );

    const idx = foundIndex >= 0 ? foundIndex : 0;

    setSelectedScreenshotIndex(idx);
    setScreenshotModalOpen(true);
  };

  // Close screenshot modal
  const closeScreenshotModal = () => {
    setScreenshotModalOpen(false);
    setSelectedScreenshotIndex(0);
    setAllScreenshotsList([]);
  };

  // Navigate to previous screenshot
  const selectPrev = () => {
    if (allScreenshotsList.length === 0) return;
    setSelectedScreenshotIndex((prev) =>
      prev === 0 ? allScreenshotsList.length - 1 : prev - 1
    );
  };

  // Navigate to next screenshot
  const selectNext = () => {
    if (allScreenshotsList.length === 0) return;
    setSelectedScreenshotIndex((prev) =>
      prev === allScreenshotsList.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <div className={styles.screenshotsPage}>
      <div className={styles.filtersContainer}>
        <div className={styles.dropdownsContainer}>
          <div className={styles.dropdown}>
            <Dropdown
              options={membersOptions}
              value={selectedMember}
              label={'SELECT MEMBER'}
              onChange={onMemberFilterChange}
              isLoading={membersLoading}
              isDisabled={membersLoading || membersError}
            />
            <div className={styles.memberErrorContainer}>
              {membersError && (
                <div className={styles.memberError}>
                  <Text
                    variant={textVariantsEnum.Text_sm}
                    fontWeight={fontWeightEnum.Medium}
                  >
                    Failed to load members. {membersErrorObj?.message || ''}
                  </Text>
                  <Button
                    color="link"
                    size="sm"
                    onClick={() => refetchMembers()}
                  >
                    Retry
                  </Button>
                </div>
              )}
            </div>
          </div>
          <div className={styles.dropdown}>
            <Dropdown
              label={'TIME'}
              options={timezoneOptions}
              value={
                timezoneOptions.find((t) => t.value === selectedTimezone) ||
                (selectedTimezone
                  ? { value: selectedTimezone, label: selectedTimezone }
                  : null)
              }
              onChange={({ value }) => handleTimezoneFilterChange(value)}
              isLoading={timezonesLoading}
            />
          </div>
        </div>
        <div className={styles.timeFilterContainer}>
          <TimeFilter range={range} setRange={setRange} />
        </div>
        <div className={styles.refreshButton} onClick={fetchTimeline}>
          <IoRefresh />
          <Text
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Medium}
          >
            Refresh
          </Text>
        </div>
      </div>
      {loading && (
        <div className="text-center my-5">
          <Spinner color="primary" />
          <p className="mt-3 text-muted">Loading sessions...</p>
        </div>
      )}
      {!loading && !sessionsByDay?.length ? (
        <div className="text-center text-muted my-5">
          No sessions found for selected filters.
        </div>
      ) : (
        !loading &&
        sessionsByDay?.length && (
          <div className={styles.sessionsListContainer}>
            <Text
              variant={textVariantsEnum.Header}
              fontWeight={fontWeightEnum.Semibold}
            >
              Screenshots
            </Text>
            {sessionsByDay?.map((session) => {
              return (
                <div
                  key={session.dayKey}
                  className={styles.individualSessionContainer}
                >
                  {session.sessions.map((individualSession) => {
                    const startTime = moment(individualSession.start)
                      .tz(selectedTimezone)
                      .format('hh:mm A');
                    const endTime = individualSession.end
                      ? moment(individualSession.end)
                          .tz(selectedTimezone)
                          .format('hh:mm A')
                      : 'Ongoing';
                    const duration = formatTimeFromSeconds(
                      individualSession.durationSec
                    );
                    const { idlePercent: idlePctNum } = normalizeIdleForDisplay(
                      individualSession.durationSec,
                      individualSession.idleSec
                    );
                    const idlePercentage = individualSession.durationSec
                      ? formatPercent(idlePctNum)
                      : '0%';
                    const activityPercentage = individualSession.activityPct;
                    // For split segments, use segment-specific screenshots (filtered by time)
                    // For regular individualSessions, use individualSession screenshots
                    const screenshots =
                      individualSession.isSplit &&
                      individualSession.parentSessionId
                        ? sessionScreenshots[individualSession.id] || []
                        : sessionScreenshots[individualSession.id] || [];
                    const isLoading =
                      individualSession.isSplit &&
                      individualSession.parentSessionId
                        ? loadingScreenshots[
                            individualSession.parentSessionId
                          ] || loadingScreenshots[individualSession.id]
                        : loadingScreenshots[individualSession.id];
                    return (
                      <div
                        key={individualSession.id}
                        className={styles.session}
                      >
                        <Accordion
                          isOpenOnStart={false}
                          onOpenChange={(open) => {
                            setExpandedSessions((prev) => {
                              const next = new Set(prev);
                              if (open) {
                                next.add(individualSession.id);
                              } else {
                                next.delete(individualSession.id);
                              }
                              return next;
                            });
                          }}
                          headingClass={styles.accordionHeading}
                          heading={
                            <div className={styles.sessionInfo}>
                              <div className={styles.bullet} />
                              <div className={styles.sessionInfoContent}>
                                {getDayLabel({
                                  dayLabel: session.dayLabel,
                                  dayKey: session.dayKey,
                                })}
                                <div className={styles.time}>
                                  {getTimeRange(
                                    startTime,
                                    endTime,
                                    duration,
                                    individualSession
                                  )}
                                  {getTimeStatistics(
                                    idlePercentage,
                                    activityPercentage
                                  )}
                                </div>
                              </div>
                            </div>
                          }
                        >
                          {getScreenshots(
                            screenshots,
                            selectedTimezone,
                            openScreenshotModal,
                            isLoading
                          )}
                        </Accordion>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )
      )}
      <CustomModal
        isVisible={screenshotModalOpen}
        onClose={closeScreenshotModal}
      >
        <ScreenshotsModal
          allScreenshotsList={allScreenshotsList}
          selectedScreenshotIndex={selectedScreenshotIndex}
          selectedTimezone={selectedTimezone}
          setSelectedScreenshotIndex={setSelectedScreenshotIndex}
        />
      </CustomModal>
      <Modal isOpen={false} toggle={closeScreenshotModal} size="lg">
        <ModalHeader toggle={closeScreenshotModal}>
          Screenshot{' '}
          {allScreenshotsList[selectedScreenshotIndex]?.dayLabel
            ? `- ${allScreenshotsList[selectedScreenshotIndex].dayLabel}`
            : ''}
        </ModalHeader>
        <ModalBody style={{ paddingLeft: '16px', paddingRight: '16px' }}>
          {allScreenshotsList.length === 0 ? (
            <div className="text-muted">No screenshot available.</div>
          ) : (
            <div>
              <div
                className="d-flex align-items-center justify-content-center mb-3 position-relative"
                style={{
                  minHeight: '280px',
                  background: '#f8f9fa',
                  border: '1px solid #e9ecef',
                  borderRadius: '8px',
                  padding: '12px',
                }}
              >
                <Button
                  color="secondary"
                  outline
                  size="sm"
                  onClick={selectPrev}
                  style={{
                    position: 'absolute',
                    left: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '44px',
                    height: '44px',
                    padding: 0,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(0,0,0,0.55)',
                    color: '#fff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    border: 'none',
                    zIndex: 10,
                  }}
                  aria-label="Previous screenshot"
                >
                  <FiChevronLeft size={22} />
                </Button>
                <div className="text-center w-100">
                  <img
                    src={getScreenshotFullSrc(
                      allScreenshotsList[selectedScreenshotIndex]
                    )}
                    alt="Screenshot preview"
                    style={{
                      maxHeight: '60vh',
                      maxWidth: '100%',
                      objectFit: 'contain',
                      borderRadius: '6px',
                    }}
                    onError={(e) => {
                      e.target.src =
                        "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect fill='%23f3f4f6' width='100' height='100'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%239ca3af' font-size='12'%3EImage not available%3C/text%3E%3C/svg%3E";
                      e.target.onerror = null;
                    }}
                  />
                  <div className="mt-2">
                    <div className="font-weight-bold">
                      {allScreenshotsList[selectedScreenshotIndex]?.timestamp
                        ? moment(
                            allScreenshotsList[selectedScreenshotIndex]
                              .timestamp
                          )
                            .tz(selectedTimezone)
                            .format('DD MMM YYYY, hh:mm A')
                        : 'Timestamp unavailable'}
                    </div>
                    <small className="text-muted">
                      {`Screenshot ${selectedScreenshotIndex + 1} of ${
                        allScreenshotsList.length
                      }`}
                    </small>
                    <div>
                      <a
                        href={getScreenshotFullSrc(
                          allScreenshotsList[selectedScreenshotIndex]
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open in new tab
                      </a>
                    </div>
                  </div>
                </div>
                <Button
                  color="secondary"
                  outline
                  size="sm"
                  onClick={selectNext}
                  style={{
                    position: 'absolute',
                    right: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '44px',
                    height: '44px',
                    padding: 0,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(0,0,0,0.55)',
                    color: '#fff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    border: 'none',
                    zIndex: 10,
                  }}
                  aria-label="Next screenshot"
                >
                  <FiChevronRight size={22} />
                </Button>
              </div>
              <div>
                <div className="mb-2 font-weight-bold">All screenshots</div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                    gap: '12px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                  }}
                >
                  {allScreenshotsList.map((item, idx) => (
                    <div
                      key={`${item.activityId || 'activity'}-${idx}`}
                      className="text-center"
                      style={{
                        border:
                          idx === selectedScreenshotIndex
                            ? '2px solid #007bff'
                            : '1px solid #e9ecef',
                        borderRadius: '6px',
                        padding: '6px',
                        cursor: 'pointer',
                        background:
                          idx === selectedScreenshotIndex ? '#e9f2ff' : '#fff',
                      }}
                      onClick={() => setSelectedScreenshotIndex(idx)}
                    >
                      <img
                        src={getScreenshotPreviewSrc(item)}
                        alt="Screenshot thumb"
                        style={{
                          width: '100%',
                          height: '120px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                        }}
                        onError={(e) => {
                          e.target.src =
                            "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect fill='%23f3f4f6' width='100' height='100'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%239ca3af' font-size='12'%3EImage not available%3C/text%3E%3C/svg%3E";
                          e.target.onerror = null;
                        }}
                      />
                      <small className="d-block mt-1 text-truncate">
                        {item.timestamp
                          ? moment(item.timestamp)
                              .tz(selectedTimezone)
                              .format('hh:mm A')
                          : 'No time'}
                      </small>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </ModalBody>
      </Modal>
    </div>
  );
};
